import * as _ from 'lodash';
import * as moment from 'moment';
import { AgGridReact } from 'ag-grid-react';
import { ReportWrapper } from './styles';
import { ICprScore } from '@nkr/models/index';
import { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-enterprise';
import { useMemo } from 'react';
import { FirstDataRenderedEvent, GridReadyEvent, SortChangedEvent } from 'ag-grid-community';
import { Utility } from '@nkr/services/utility';

interface IProps {
    data: ICprScore[],
    onGridReady: (e: GridReadyEvent) => void
}

const Grid = (props: IProps) => {
    const getTotals = (): ICprScore => {
        let totalCPs            = _.sumBy(props.data, item => item.compatiblePairCount);
        let totalTxps           = _.sumBy(props.data, item => item.includedCount);
        let totalProtected      = _.sumBy(props.data, item => item.allDonorsProtected === true ? 1 : 0);
        let totalVouchersLast12 = _.sumBy(props.data, item => item.vouchersLast12);
        let totalCprScore       = totalCPs / totalTxps;

        return {
            centerId: 0,
            centerAlias: 'TOTALS:',
            compatiblePairCount: totalCPs,
            includedCount: totalTxps,
            allDonorsProtected: totalProtected,
            allDonorsProtectedEndDate: null,
            vouchersLast12: totalVouchersLast12,
            cprScore: totalCprScore
        };
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    }

    const onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells();
    };

    const columns: ColDef[] = [
        {
            colId: 'rank',
            headerName: 'R#',
            width: 10,
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.node.rowPinned == 'bottom') {
                    return null;
                }

                return <>{params.value}</>
            },
            valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1
        },

        {
            colId: 'centerAlias',
            field: 'centerAlias',
            headerName: 'Transplant Center',
            filter: true,
            filterParams: {},
            comparator: Utility.stringSortComparator,
            width: 30,
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.node.rowPinned == 'bottom') {
                    return <>{params.value}</>;
                }

                return (
                    <><a href={`/report/cpr-score-audit-report/${params.data.centerId}`} target='_blank'>{params.value}</a></>
                );
            }
        },

        {
            colId: 'compatiblePairCount',
            field: 'compatiblePairCount',
            headerName: 'Compatible Pairs',
            filter: true,
            filterParams: {},
            sortingOrder: ['desc','asc',null],
            width: 30,
        },

        {
            colId: 'includedCount',
            field: 'includedCount',
            headerName: 'Included Transplants',
            filter: 'agNumberColumnFilter',
            filterParams: {},
            sortingOrder: ['desc','asc',null],
            width: 30
        },

        {
            colId: 'cprScore',
            field: 'cprScore',
            headerName: 'CPR Score',
            filter: 'agNumberColumnFilter',
            filterParams: {},
            sortingOrder: ['desc','asc',null],
            width: 30,
            cellRendererFramework: (params: ICellRendererParams) => {
                var percent = params.value * 100,
                    display = `${Math.floor(percent)}%`
                ;
                return (<>{display}</>);
            }
        },

        {
            colId: 'vouchersLast12',
            field: 'vouchersLast12',
            headerName: 'SV Donations Prior 12 Months',
            sortingOrder: ['desc','asc',null],
            width: 30
        },

        {
            colId: 'allDonorsProtected',
            field: 'allDonorsProtected',
            headerName: 'All Donors Protected',
            sortingOrder: ['desc','asc',null],
            width: 30,
            cellRendererFramework: (params: ICellRendererParams) => {
                const row       = params.data as ICprScore,
                    centerId    = row.centerId,
                    endDate     = row.allDonorsProtectedEndDate
                ;
                if( typeof params.value === 'number' && !isNaN(params.value) ) {
                    return <span className='text-muted'>{params.value}</span>;
                }

                switch(params.value) {
                    case true:
                        return <a href={`/report/donors-protected?ctrID=${centerId}&endDate=${endDate}`} className='label label-success' target='_blank'>Yes</a>;
                    case false:
                        return <a href={`/report/donors-protected?ctrID=${centerId}&endDate=${endDate}`} className='label label-warning' target='_blank'>No</a>;
                    default:
                        return <span className='text-muted'>- not processed -</span>;
                }
            }
        }
    ];

    const totals = getTotals();

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => props.data, [props.data]);
    const pinnedRowData = useMemo(() => totals, [totals]);

    return (
        <ReportWrapper className='ag-theme-bootstrap'>
            <AgGridReact
                defaultColDef={{
                    headerClass: 'list-header',
                    cellClass: 'list-cell',
                    sortable: true,
                    resizable: false
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                pinnedBottomRowData={[pinnedRowData]}
                headerHeight={36}
                rowHeight={30}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                noRowsOverlayComponentFramework={() => <div>No CPR Data...</div>}
                onGridReady={props.onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onSortChanged={onSortChanged}
                getRowStyle={e => {
                    if (e.node.rowPinned == 'bottom') {
                        return { backgroundColor: 'lightblue' };
                    }

                    return null;
                }}
            />
        </ReportWrapper>
    );
}

export default Grid;